/* eslint-disable */
import React, {
  FC,
  useEffect,
  useRef,
  useState,
  useReducer,
  useContext,
} from "react";
import { createUseStyles, useTheme } from "react-jss";

import PanoPlayer from "Components/PanoPlayer";
import { MapContext } from "Context/MapContext";

import ShareMenuContainer from "Containers/ShareMenu";
import { motion } from "framer-motion";

import StartPanning from "Assets/images/start_panning.svg";
import PausePanning from "Assets/images/pause_panning.svg";
import Info from "Assets/images/info.svg";
import EnterFullScreen from "Assets/images/enter_full_screen.svg";
import ExitFullScreen from "Assets/images/exit_full_screen.svg";
import styles from "./styles";
import { sanitize } from "dompurify";

interface Props {
  media: Array<Object>;
  selectedIdx: number;
  changePanoFullScreen: any;
}

const PanoViewer: FC<Props> = (props: Props) => {
  const useStyles = createUseStyles(styles);

  const theme = useTheme();

  const classes = useStyles({ theme, ...props });

  const { media, selectedIdx, changePanoFullScreen } = props;
  const [fullScreen, setFullScreen] = useState(false);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const play = useRef(true);
  const [info, showInfo] = useState(false);
  const panoRef = useRef(null);
  const { instId, locId } = useContext(MapContext);
  const shareUrl = `${process.env.REACT_APP_API_BASE_URL}tour/panoramas/${instId}/${locId}?id=${media[selectedIdx]?.["id"]}`;

  useEffect(() => {
    setFullScreen(false);
    play.current = true;
    forceUpdate();
  }, [selectedIdx]);

  const changeMode = () => {
    play.current = !play.current;
    forceUpdate();
  };

  const changeFullScreen = (val: boolean) => {
    setFullScreen(val);
  };

  const animations = {
    open: {
      x: "0%",
      transition: {
        ease: [0.28, 0.57, 0.2, 1],
        duration: 0.5,
      },
    },
    closed: {
      x: "100%",
      transition: {
        ease: "easeIn",
        duration: 0.25,
      },
    },
  };

  return (
    <>
      <PanoPlayer
        ref={panoRef}
        fullScreen={fullScreen}
        changePanoFullScreen={changePanoFullScreen}
        play={play.current}
        changeMode={changeMode}
        changeFullScreen={changeFullScreen}
        options={{
          hasEasy: media[selectedIdx]["hasEasy"],
          height: "683",
          width: "1341",
          path: media[selectedIdx]["path"],
          startFov: media[selectedIdx]["start_fov"],
          startLat: media[selectedIdx]["start_lat"],
          startLon: media[selectedIdx]["start_lon"],
          exit_lat: media[selectedIdx]["exit_lat"],
          exit_lon: media[selectedIdx]["exit_lon"],
          autoRotateSpeed: 0.3,
        }}
        data-testid="pano"
      >
        <div
          tabIndex={-1}
          aria-hidden="true"
          className={classes.rightContainer}
        >
          <div
            className={classes.panning}
            onClick={changeMode}
            onKeyDown={changeMode}
            role="button"
            tabIndex={-1}
            aria-hidden="true"
          >
            {play.current ? <PausePanning /> : <StartPanning />}
          </div>
          <div
            tabIndex={-1}
            aria-hidden="true"
            className={classes.fullScreenStyle}
            onClick={() => {
              changeFullScreen(!fullScreen);
              changePanoFullScreen(!fullScreen);
            }}
            onKeyDown={(e: any) => {
              if (e.code === "Enter" || e.code === "Space") {
                changeFullScreen(!fullScreen);
                changePanoFullScreen(!fullScreen);
              }
            }}
            role="button"
          >
            {fullScreen ? <ExitFullScreen /> : <EnterFullScreen />}
          </div>
        </div>
        <div
          id={"carousel-title"}
          className={classes.imgText}
          data-testid="title"
          tabIndex={0}
        >
          {media[selectedIdx]["title"]}
        </div>
        {media[selectedIdx]["description"] && (
          <div className={classes.infoContainer}>
            {!fullScreen && (
              <button
                aria-label="Open Description"
                className={classes.infoIcon}
                onClick={() => showInfo(!info)}
                role="button"
                tabIndex={0}
                data-testid="info"
                onBlur={() => {
                  !info &&
                    document
                      .getElementById(`carouselImages-${selectedIdx + 1}`)
                      .focus();
                }}
              >
                <Info />
              </button>
            )}
            {info && (
              <motion.div
                initial={{ x: "100%" }}
                animate={info ? "open" : "closed"}
                variants={animations}
                className={classes.infoPanel}
              >
                <span
                  className={classes.infoDescription}
                  tabIndex={0}
                  id="carousel-description"
                  data-testid="description"
                  onBlur={() => {
                    document
                      .getElementById(`carouselImages-${selectedIdx + 1}`)
                      .focus();
                  }}
                  dangerouslySetInnerHTML={{
                    __html: sanitize(media[selectedIdx]["description"], {
                      ADD_ATTR: ["target"],
                    }),
                  }}
                ></span>
              </motion.div>
            )}
          </div>
        )}
        )
        <div>
          <span
            tabIndex={-1}
            aria-hidden="true"
            style={{
              cursor: "pointer",
              position: "absolute",
              bottom: "0%",
              right: "0%",
            }}
          >
            <ShareMenuContainer url={shareUrl} svgColor="#FFFFFF" />
          </span>
        </div>
      </PanoPlayer>
    </>
  );
};

export default PanoViewer;
